import { useEffect, useState } from 'react';

import i18next from 'i18next';
import type { ReactNode } from 'react';

import { GoogleOAuthProvider } from '@react-oauth/google';

import { useSelectConfigLoadError, useSelectGoogleSettings } from 'Config';
import { getAppConfig } from 'Config/configSlice';
import { initializeSentry } from 'Config/sentry-config';
import { store, useAppDispatch } from 'Store';
import { hydratePreferencesFromLocalStorage } from 'User/data';
import { getLanguageFromLocalStorage } from 'Utils/localStorageUtils';

type InitializerProps = {
  children: ReactNode;
};

export const Initializer = ({ children }: Readonly<InitializerProps>) => {
  const dispatch = useAppDispatch();
  const googleSettings = useSelectGoogleSettings();
  const configLoadError = useSelectConfigLoadError();

  const [isInitialized, setIsInitialized] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        dispatch(hydratePreferencesFromLocalStorage());

        const savedLanguageFromStorage = getLanguageFromLocalStorage();
        if (savedLanguageFromStorage && i18next.language !== savedLanguageFromStorage) {
          await i18next.changeLanguage(savedLanguageFromStorage);
        }

        await dispatch(getAppConfig()).unwrap();

        const currentState = store.getState();
        const currentSentrySettings = currentState.config.sentrySettings;

        initializeSentry({
          sentrySettings: currentSentrySettings
        });

        setIsInitialized(true);
      } catch (error: any) {
        console.error('Failed to initialize application:', error);
        setHasError(true);
        setIsInitialized(true);
      } finally {
        const htmlLoader = document.getElementById('html-loader');
        if (htmlLoader) {
          htmlLoader.style.display = 'none';
        }
      }
    };

    if (!isInitialized) {
      initializeApp();
    }
  }, [dispatch, isInitialized]);

  if (!isInitialized) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '16px',
          fontFamily: 'Roboto, sans-serif'
        }}
      >
        Initializing application...
      </div>
    );
  }

  if (hasError || configLoadError) {
    console.warn('Application initialized with errors, but continuing...', {
      hasError,
      configLoadError
    });
  }

  const hasValidGoogleConfig = googleSettings?.clientId && googleSettings.clientId.trim() !== '';

  return (
    <>
      {hasValidGoogleConfig ? (
        <GoogleOAuthProvider clientId={googleSettings.clientId}>{children}</GoogleOAuthProvider>
      ) : (
        <>{children}</>
      )}
    </>
  );
};
